import { useState, useEffect, useCallback } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit2,
  Trash2,
  LogOut,
  TimerOff,
} from "lucide-react";
import { businessApi } from "../../../services/api";
import DatePickerInput from "../../../components/DatePickerInput";
import { useBusinessAuth } from "../../context/BusinessAuthContext";
import ShopSelect from "../../../components/ShopSelect";
import RequestReviewList from "./components/RequestReviewList";
import RequestDecisionDialog from "./components/RequestDecisionDialog";
import {
  Button, ConfirmDialog, Dialog, EmptyState, ErrorState, IconButton, Notice, PageHeader, Pagination, SearchInput, SkeletonRows, Status, Surface, plainError,
  Th, THead, useSelectedTabInView,
} from "../../../components/ui";
import { focusRing } from "../../../components/ui/cn";
import { describeCorrection, describeOvertime, formatClock } from "./myHrText";
import { OvertimeModal } from "./components/LeaveOvertimeModals";
import ScheduledOvertimePanel, { ScheduleOvertimeDialog } from "./components/ScheduledOvertimePanel";
import OvertimeReviewPanel from "./components/OvertimeReviewPanel";

// Attendance statuses in words. Colour only where a reviewer should stop: late and absent.
const ATTENDANCE_STATUS = {
  present: { label: "Present", tone: "neutral" },
  late: { label: "Late", tone: "warning" },
  absent: { label: "Absent", tone: "danger" },
  half_day: { label: "Half day", tone: "neutral" },
  on_leave: { label: "On leave", tone: "neutral" },
  holiday: { label: "Holiday", tone: "neutral" },
  excused: { label: "Excused", tone: "neutral" },
};

function todayLocalISO() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeDateOnly(value) {
  if (!value) return null;
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
}

function formatDateValue(value) {
  const normalized = normalizeDateOnly(value);
  if (!normalized) return "—";
  const parsed = new Date(`${normalized}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? normalized
    : parsed.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "2-digit" });
}

/* ─── Manual Entry Modal ────────────────────────────────── */
function ManualEntryModal({ open, onClose, onSubmit, members, initial }) {
  const [form, setForm] = useState({ business_member_id: "", date: todayLocalISO(), check_in: "", check_out: "", status: "", notes: "", reason: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...initial, reason: "" } : { business_member_id: "", date: todayLocalISO(), check_in: "", check_out: "", status: "", notes: "", reason: "" });
  }, [open, initial]);

  if (!open) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const editing = !!initial?.id;
  const inputCls = "h-control w-full rounded-control border border-default bg-surface px-3 text-body text-foreground placeholder:text-tertiary hover:border-strong focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-touch";

  const handleSubmit = async () => {
    if (saving) return;
    const payload = { ...form };
    if (!payload.status) delete payload.status;
    if (!payload.check_out) delete payload.check_out;
    if (!payload.notes) delete payload.notes;
    if (!payload.check_in) delete payload.check_in;
    payload.reason = String(payload.reason || "").trim();
    payload.business_member_id = Number(payload.business_member_id);
    setSaving(true);
    try { await onSubmit(payload); } finally { setSaving(false); }
  };

  const submitDisabled = (!editing && (!form.business_member_id || !form.date || (!form.check_in && !["absent", "on_leave"].includes(form.status)))) || !String(form.reason || "").trim();

  return (
    <Dialog
      open
      onOpenChange={(next) => { if (!next && !saving) onClose(); }}
      title={editing ? "Edit attendance" : "Record attendance for an employee"}
      size="md"
      footer={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form="attendance-entry-form" variant="primary" loading={saving} disabled={submitDisabled}>
            {editing ? "Save changes" : "Record attendance"}
          </Button>
        </>
      )}
    >
      <form id="attendance-entry-form" onSubmit={(event) => { event.preventDefault(); handleSubmit(); }} className="text-body-sm text-foreground">
        <p className="mb-4 text-body-sm text-secondary">Times are in the store's time zone. Paid time is worked out from the employee's schedule. You can't change your own attendance here; use My HR.</p>

        {!editing && (
          <>
            <label className="mb-1 block text-body-sm font-medium">Employee *</label>
            <ShopSelect
              value={form.business_member_id}
              onValueChange={(value) => setForm((current) => ({ ...current, business_member_id: value }))}
              options={[
                { value: "", label: "— Select —" },
                ...members.map((member) => {
                  const name = member.name || [member.firstName, member.lastName].filter(Boolean).join(" ") || member.email || `#${member.id}`;
                  return { value: member.id, label: String(member.status || "").toLowerCase() === "terminated" ? `${name} (left)` : name };
                }),
              ]}
              ariaLabel="Employee"
              align="start"
              minPanelWidth={240}
              triggerClassName="mb-3 min-h-10 w-full rounded-control px-3 py-2 text-body-sm"
              panelClassName="py-1"
            />
          </>
        )}

        <label className="mb-1 block text-body-sm font-medium">Date *</label>
        <DatePickerInput value={form.date} onValueChange={(value) => set("date")({ target: { value } })} required max={todayLocalISO()} title="SELECT ATTENDANCE DATE" ariaLabel="Attendance date" className={`mb-3 ${inputCls}`} />

        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-body-sm font-medium">Check In</label>
            <input type="time" step="60" value={form.check_in || ""} onChange={set("check_in")} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-body-sm font-medium">Check Out</label>
            <input type="time" step="60" value={form.check_out || ""} onChange={set("check_out")} className={inputCls} />
          </div>
        </div>

        <label className="mb-1 block text-body-sm font-medium">Status</label>
        <ShopSelect
          value={form.status || ""}
          onValueChange={(value) => setForm((current) => ({ ...current, status: value }))}
          options={[
            { value: "", label: "Auto-detect" },
            ...["present", "late", "absent", "half_day", "on_leave"].map((status) => ({ value: status, label: status.replace("_", " ") })),
          ]}
          ariaLabel="Status"
          align="start"
          minPanelWidth={180}
          triggerClassName="mb-3 min-h-10 w-full rounded-control px-3 py-2 text-body-sm"
          panelClassName="py-1"
        />

        <label className="mb-1 block text-body-sm font-medium">Notes</label>
        <textarea value={form.notes || ""} onChange={set("notes")} rows={2} maxLength={500} className={`mb-3 min-h-20 ${inputCls}`} />

        <label htmlFor="attendance-reason" className="mb-1 block text-body-sm font-medium">Reason for this change *</label>
        <textarea id="attendance-reason" value={form.reason || ""} onChange={set("reason")} rows={2} maxLength={500} placeholder="Kept in the activity log" className={`min-h-20 ${inputCls}`} />
      </form>
    </Dialog>
  );
}

/* ─── Helpers ───────────────────────────────────────────── */
function fmtTime(t) {
  if (!t) return "—";
  const raw = String(t).trim();
  if (/^\d{2}:\d{2}(?::\d{2})?$/.test(raw)) {
    const parts = raw.split(":");
    const h = Number(parts[0]);
    const m = parts[1];
    return `${h % 12 || 12}:${m} ${h < 12 ? "AM" : "PM"}`;
  }
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw) ? raw.replace(" ", "T") : raw;
  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleTimeString("en-PH", { hour: "numeric", minute: "2-digit" });
  }
  return raw;
}
function fmtMinutes(min) {
  if (!min && min !== 0) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/* ─── Main Page ─────────────────────────────────────────── */

export default function HRAttendance() {
  const auth = useBusinessAuth();
  const authRole = String(auth?.role || "").toUpperCase();
  const canReadAttendance = authRole === "OWNER" || auth?.hasPermission?.("hr:attendance:read");
  // An employee's own attendance lives in My HR (one place for clocking, history and corrections).
  if (!canReadAttendance) {
    return <Navigate to="/business/hr/me/attendance" replace />;
  }

  return <AttendanceManagementView />;
}

function AttendanceManagementView() {
  const auth = useBusinessAuth();
  const myMemberId = Number(auth?.currentBusiness?.membership_id || 0);
  const canRecord = String(auth?.role || "").toUpperCase() === "OWNER" || auth?.hasPermission?.("attendance:record");
  const [searchParams, setSearchParams] = useSearchParams();
  const isOwner = String(auth?.role || "").toUpperCase() === "OWNER";
  const canApproveOvertime = isOwner || auth?.hasPermission?.("hr:overtime:approve");
  const canAssignOvertime = isOwner || auth?.hasPermission?.("hr:overtime:assign");
  const canSeeOvertime = canApproveOvertime || canAssignOvertime || auth?.hasPermission?.("hr:attendance:read");
  const requestedView = searchParams.get("view");
  let view = "records";
  if (requestedView === "corrections" && canRecord) view = "corrections";
  if (requestedView === "overtime" && canSeeOvertime) view = "overtime";
  if (requestedView === "scheduled-overtime" && canSeeOvertime) view = "scheduled-overtime";
  if (requestedView === "overtime-review" && canSeeOvertime) view = "overtime-review";
  const setView = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "records") params.delete("view"); else params.set("view", next);
    setSearchParams(params);
    setNotice("");
  };
  const [pendingCorrections, setPendingCorrections] = useState(null);
  const [needsAttention, setNeedsAttention] = useState(false);
  const [notice, setNotice] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [failure, setFailure] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const loadCorrections = useCallback((params) => businessApi.getAttendanceCorrections(params), []);
  const decideCorrection = useCallback((id, decision, body) => businessApi.decideAttendanceCorrection(id, decision, body), []);
  const [pendingOvertime, setPendingOvertime] = useState(null);
  const [assigningOvertime, setAssigningOvertime] = useState(false);
  const [overtimeReload, setOvertimeReload] = useState(0);
  const loadOvertime = useCallback((params) => businessApi.getOvertimeRequests(params), []);
  const decideOvertime = useCallback((id, decision, body) => businessApi.decideOvertime(id, decision, body), []);
  // Report 70: scheduled overtime and overtime days that need a decision before payroll.
  const [pendingScheduled, setPendingScheduled] = useState(null);
  const [openReviews, setOpenReviews] = useState(null);
  const [schedulingOvertime, setSchedulingOvertime] = useState(false);
  const [scheduledReload, setScheduledReload] = useState(0);
  const tabRow = useSelectedTabInView('[aria-selected="true"]', `${view}|${pendingCorrections ?? ""}|${pendingOvertime ?? ""}|${pendingScheduled ?? ""}|${openReviews ?? ""}`);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [date, setDate] = useState(todayLocalISO());
  const [dateEnd, setDateEnd] = useState(""); // empty = single-date mode
  const [queryEmp, setQueryEmp] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showManual, setShowManual] = useState(false);
  const [editing, setEditing] = useState(null);
  const limit = 20;

  const rangeMode = dateEnd && dateEnd !== date;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = needsAttention ? { page, limit, needsAttention: true } : { page, limit, dateFrom: date, dateTo: dateEnd || date };
      if (statusFilter) params.status = statusFilter;

      const [rec, sum, mem] = await Promise.all([
        businessApi.getAttendanceRecords(params),
        businessApi.getAttendanceSummary({ dateFrom: date, dateTo: dateEnd || date }),
        businessApi.getMembers(),
      ]);
      setRecords(rec.records || []);
      setTotal(rec.pagination?.total || 0);
      setSummary(sum.summary || {});
      setMembers(mem.members || mem.data || []);
      setLoadFailed(false);
    } catch {
      // A failed load is an error with a retry, never an empty day.
      setRecords([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [page, date, dateEnd, statusFilter, needsAttention]);

  useEffect(() => { setPage(1); }, [date, dateEnd, statusFilter, needsAttention]);
  useEffect(() => {
    if (!canRecord) return;
    businessApi.getAttendanceCorrections({ status: "pending" }).then((res) => setPendingCorrections((res?.requests || []).length)).catch(() => {});
  }, [canRecord]);
  useEffect(() => {
    if (!canSeeOvertime) return;
    businessApi.getOvertimeRequests({ status: "pending" }).then((res) => setPendingOvertime((res?.requests || []).length)).catch(() => {});
    businessApi.getScheduledOvertime({ status: "pending" }).then((res) => setPendingScheduled((res?.authorizations || []).length)).catch(() => {});
    businessApi.getOvertimeReviewItems({ state: "open" }).then((res) => setOpenReviews((res?.items || []).length)).catch(() => {});
  }, [canSeeOvertime]);
  useEffect(() => { load(); }, [load]);

  const employeeOf = (rec) => rec?.member_name || "this employee";

  const handleManualSubmit = async (data) => {
    try {
      if (editing) {
        await businessApi.editAttendance(editing.id, data);
        setNotice("Attendance updated. The earlier times stay in the activity log.");
      } else {
        await businessApi.attendanceManualEntry(data);
        setNotice("Attendance recorded.");
      }
      setShowManual(false);
      setEditing(null);
      load();
    } catch (err) {
      setFailure({ title: "Attendance not saved", text: plainError(err, "The attendance could not be saved. Try again.") });
    }
  };

  const shiftDate = (dir) => {
    const d = new Date(date);
    d.setDate(d.getDate() + dir);
    setDate(d.toISOString().slice(0, 10));
  };

  const totalPages = Math.ceil(total / limit) || 1;
  const filteredRecords = queryEmp
    ? records.filter((r) => (r.member_name || "").toLowerCase().includes(queryEmp.toLowerCase()))
    : records;

  // Counts for the dates in view, as one line of words rather than tiles.
  const summaryByStatus = {};
  if (Array.isArray(summary)) {
    summary.forEach((row) => { summaryByStatus[row.status] = Number(row.count); });
  }
  const workingNow = records.filter((r) => r.check_in && !r.check_out && r.attention !== "missing_clock_out").length;
  const counts = [
    ["Present", summaryByStatus.present], ["Late", summaryByStatus.late], ["Absent", summaryByStatus.absent],
    ["Half day", summaryByStatus.half_day], ["On leave", summaryByStatus.on_leave], ["Working now", workingNow],
  ].filter(([, value]) => Number(value) > 0);

  const views = [
    ["records", "Records", true, null],
    ["corrections", "Correction requests", canRecord, pendingCorrections],
    ["overtime", "Overtime", canSeeOvertime, pendingOvertime],
    ["scheduled-overtime", "Scheduled overtime", canSeeOvertime, pendingScheduled],
    ["overtime-review", "Overtime to review", canSeeOvertime, openReviews],
  ].filter(([, , visible]) => visible);
  const toolButton = `inline-flex h-control items-center rounded-control border border-default bg-surface px-3 text-control-sm text-foreground hover:border-strong max-md:min-h-touch ${focusRing}`;
  const pressed = "border-accent bg-accent-subtle text-accent";

  return (
    <div className="min-w-0 space-y-6">
      <PageHeader
        title="Attendance"
        description="Review time records, fix days that need attention, and decide employees' correction and overtime requests."
        actions={view === "records" && canRecord ? (
          <Button variant="primary" icon={Plus} onClick={() => { setEditing(null); setShowManual(true); }}>Record attendance</Button>
        ) : view === "overtime" && canAssignOvertime ? (
          <Button variant="primary" icon={Plus} onClick={() => setAssigningOvertime(true)}>Assign overtime</Button>
        ) : view === "scheduled-overtime" && canAssignOvertime ? (
          <Button variant="primary" icon={Plus} onClick={() => setSchedulingOvertime(true)}>Schedule overtime</Button>
        ) : null}
        navigation={views.length > 1 ? (
          <div ref={tabRow} role="tablist" aria-label="Attendance views" className="-mb-px flex gap-6 overflow-x-auto overflow-y-hidden border-b border-subtle">
            {views.map(([key, label, , count]) => (
              <button
                key={key}
                type="button"
                role="tab"
                data-ui="button"
                aria-selected={view === key}
                onClick={() => setView(key)}
                className={`relative inline-flex h-10 shrink-0 items-center gap-1.5 text-body-sm font-medium max-md:h-touch after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full ${focusRing} ${view === key ? "text-foreground after:bg-accent" : "text-secondary after:bg-transparent hover:text-foreground"}`}
              >
                {label}
                {count ? <span className="text-meta tabular-nums text-tertiary">{count}</span> : null}
              </button>
            ))}
          </div>
        ) : null}
      />

      {notice ? <Notice tone="success" role="status">{notice}</Notice> : null}

      {view === "scheduled-overtime" ? (
        <section aria-label="Scheduled overtime" className="space-y-3">
          <p className="max-w-[90ch] text-body-sm text-secondary">
            Overtime authorised in advance on regular days, on top of the ordinary shift (at most 8 paid hours). Only time actually worked inside it is paid; extra time beyond it is asked for and approved day by day.
          </p>
          <ScheduledOvertimePanel
            canApprove={canApproveOvertime}
            myMemberId={myMemberId}
            reloadKey={scheduledReload}
            onPendingCount={setPendingScheduled}
            onNotice={setNotice}
          />
          {schedulingOvertime ? (
            <ScheduleOvertimeDialog
              canApprove={canApproveOvertime}
              employees={members
                .filter((m) => String(m.status || "").toLowerCase() === "active" && Number(m.id) !== myMemberId)
                .map((m) => ({ id: m.id, name: m.name || [m.firstName, m.lastName].filter(Boolean).join(" ") || m.email || `#${m.id}` }))}
              onClose={() => setSchedulingOvertime(false)}
              onSaved={(created) => {
                setSchedulingOvertime(false);
                setScheduledReload((key) => key + 1);
                setNotice(created?.status === "approved"
                  ? "Scheduled overtime approved. The employee sees it in My HR."
                  : "Scheduled overtime sent for approval.");
              }}
            />
          ) : null}
        </section>
      ) : view === "overtime-review" ? (
        <section aria-label="Overtime to review" className="space-y-3">
          <OvertimeReviewPanel canDecide={canApproveOvertime} onOpenCount={setOpenReviews} onNotice={setNotice} />
        </section>
      ) : view === "overtime" ? (
        <section aria-label="Overtime" className="space-y-3">
          <p className="max-w-[90ch] text-body-sm text-secondary">
            Time after a shift is recorded, but only approved overtime is paid. Approve or reject what employees ask for, or assign overtime.
          </p>
          <RequestReviewList
            kind="overtime"
            load={loadOvertime}
            decide={decideOvertime}
            reloadKey={overtimeReload}
            onPendingCount={setPendingOvertime}
            canDecide={canApproveOvertime}
            withdrawLabel="Withdraw approval"
            emptyText="No overtime is waiting for approval."
            approveText={(request) => `${request.memberName || "This employee"}: ${describeOvertime(request)}. Only the part they actually work in this time is paid.`}
            summarize={(request) => (
              <>
                {describeOvertime(request)}
                <span className="block text-secondary">{request.origin === "assigned" ? `Assigned by ${request.requestedByName || "a manager"}` : "Asked by the employee"}</span>
              </>
            )}
          />
          {assigningOvertime ? (
            <OvertimeModal
              mode="assign"
              canApprove={canApproveOvertime}
              employees={members
                .filter((m) => String(m.status || "").toLowerCase() === "active" && Number(m.id) !== myMemberId)
                .map((m) => ({ id: m.id, name: m.name || [m.firstName, m.lastName].filter(Boolean).join(" ") || m.email || `#${m.id}` }))}
              onClose={() => setAssigningOvertime(false)}
              onSubmitted={(created) => {
                setAssigningOvertime(false);
                setOvertimeReload((key) => key + 1);
                setNotice(created?.status === "approved"
                  ? "Overtime assigned and approved. The employee sees it in My HR."
                  : "Overtime assigned. It now waits for someone who can approve overtime.");
              }}
            />
          ) : null}
        </section>
      ) : view === "corrections" ? (
        <section aria-label="Correction requests" className="space-y-3">
          <p className="max-w-[90ch] text-body-sm text-secondary">
            Employees ask for their attendance to be fixed. Approving replaces the recorded times; the old times stay in the history.
          </p>
          <RequestReviewList
            kind="attendance correction"
            load={loadCorrections}
            decide={decideCorrection}
            onPendingCount={setPendingCorrections}
            emptyText="No correction requests are waiting."
            approveText={(request) => `${describeCorrection(request)} is recorded for ${request.memberName || "this employee"}, replacing what is there now. Paid time is worked out from their schedule.`}
            summarize={(request) => {
              const times = (fact, none) => (fact?.checkIn ? `${formatClock(fact.checkIn)} – ${fact.checkOut ? formatClock(fact.checkOut) : "no clock-out"}` : none);
              return (
                <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5">
                  <dt className="text-secondary">Requested</dt>
                  <dd>{describeCorrection(request)}</dd>
                  {request.current ? <><dt className="text-secondary">Recorded now</dt><dd>{times(request.current, "No attendance recorded")}</dd></> : null}
                  {request.original ? <><dt className="text-secondary">Before approval</dt><dd>{times(request.original, "No attendance recorded")}</dd></> : null}
                </dl>
              );
            }}
          />
        </section>
      ) : (
        <section aria-label="Attendance records" className="min-w-0 space-y-4">
          <Surface
            toolbar={
              <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <IconButton icon={ChevronLeft} label="Previous day" variant="secondary" onClick={() => shiftDate(-1)} />
            <DatePickerInput value={date} onValueChange={setDate} title="SELECT ATTENDANCE DATE" ariaLabel="Attendance filter date" className="rounded-control border border-default bg-surface px-3 py-2 text-body-sm text-foreground max-md:min-h-touch" />
            {rangeMode ? (
              <>
                <span className="text-meta text-secondary">to</span>
                <DatePickerInput value={dateEnd} min={date} onValueChange={setDateEnd} title="SELECT RANGE END" ariaLabel="Attendance range end" className="rounded-control border border-default bg-surface px-3 py-2 text-body-sm text-foreground max-md:min-h-touch" />
              </>
            ) : null}
            <IconButton icon={ChevronRight} label="Next day" variant="secondary" onClick={() => shiftDate(1)} />
            <button type="button" data-ui="button" className={toolButton} onClick={() => { setDate(todayLocalISO()); setDateEnd(""); }}>Today</button>
            <button type="button" data-ui="button" aria-pressed={rangeMode} className={`${toolButton} ${rangeMode ? pressed : ""}`} onClick={() => setDateEnd(rangeMode ? "" : date)}>Date range</button>
            <button type="button" data-ui="button" aria-pressed={needsAttention} className={`${toolButton} ${needsAttention ? pressed : ""}`} onClick={() => setNeedsAttention((value) => !value)}>Needs attention</button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-[200px] flex-1 sm:max-w-sm">
              <SearchInput label="Search by employee" placeholder="Search by employee" value={queryEmp} onChange={(event) => setQueryEmp(event.target.value)} />
            </div>
            <ShopSelect
              value={statusFilter}
              onValueChange={setStatusFilter}
              options={[
                { value: "", label: "All statuses" },
                ...["present", "late", "absent", "half_day", "on_leave"].map((status) => ({ value: status, label: ATTENDANCE_STATUS[status].label })),
              ]}
              ariaLabel="Filter attendance by status"
              align="start"
              minPanelWidth={160}
              triggerClassName="min-h-10 w-40 rounded-control px-3 py-2 text-body-sm max-md:min-h-[44px]"
              panelClassName="py-1"
            />
            {counts.length ? (
              <p className="text-meta tabular-nums text-secondary" aria-label="Attendance in view">
                {counts.map(([label, value]) => `${label} ${value}`).join(" · ")}
              </p>
            ) : null}
          </div>

              </div>
            }
          >
          {loading ? (
            <div><SkeletonRows rows={5} columns={5} label="Loading attendance" /></div>
          ) : loadFailed ? (
            <div>
              <ErrorState title="Attendance couldn't be loaded" description="Check your connection and try again. Nothing has changed." onRetry={load} />
            </div>
          ) : filteredRecords.length === 0 ? (
            <div>
              <EmptyState
                title={needsAttention ? "Nothing needs attention" : "No attendance recorded"}
                description={needsAttention ? "No record in these dates has a missing clock-out or another problem." : `Nothing is recorded for ${rangeMode ? "these dates" : formatDateValue(date)}. A scheduled day with no record counts as absent.`}
              />
            </div>
          ) : (
            <div className="relative overflow-x-auto rounded-b-panel">
              <table data-ui="table" className="w-full min-w-[760px] text-left text-cell text-foreground">
                <caption className="sr-only">Attendance records</caption>
                <THead>
                  <tr>
                    <Th className="sticky left-0 bg-surface-sunken">Employee</Th>
                    {(rangeMode || needsAttention) ? <Th>Date</Th> : null}
                    <Th>Clock in</Th>
                    <Th>Clock out</Th>
                    <Th numeric>Paid time</Th>
                    <Th numeric>Late</Th>
                    <Th>After shift / approved overtime</Th>
                    <Th>Status</Th>
                    <Th numeric><span className="sr-only">Change</span></Th>
                  </tr>
                </THead>
                <tbody className="divide-y divide-subtle">
                  {filteredRecords.map((r) => {
                    const state = ATTENDANCE_STATUS[r.status] || { label: String(r.status || "—").replace(/_/g, " "), tone: "neutral" };
                    const own = r.business_member_id === myMemberId;
                    return (
                      <tr key={r.id}>
                        <th scope="row" className="sticky left-0 bg-surface px-4 py-2.5 text-left font-medium">
                          {r.member_name}
                          {r.department_name ? <span className="block text-meta font-normal text-secondary">{r.department_name}</span> : null}
                        </th>
                        {(rangeMode || needsAttention) ? <td className="whitespace-nowrap px-4 py-2.5">{formatDateValue(r.date)}</td> : null}
                        <td className="whitespace-nowrap px-4 py-2.5 tabular-nums">{fmtTime(r.checkInLocal)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 tabular-nums">
                          {r.check_out ? fmtTime(r.checkOutLocal)
                            : r.attention === "missing_clock_out" ? <Status tone="warning" icon className="font-medium">Missing clock-out</Status>
                              : !r.check_in ? "—" : <span className="text-secondary">Working now</span>}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">{fmtMinutes(r.worked_minutes)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
                          {/* Report 57: recorded lateness counts from the shift start; minutes inside the grace are on time and shown apart. */}
                          {(r.classification?.lateMinutes ?? r.late_minutes) > 0 ? fmtMinutes(r.classification?.lateMinutes ?? r.late_minutes)
                            : r.classification?.actualLateMinutes > 0 ? <span className="text-secondary">On time <span className="block text-meta">{fmtMinutes(r.classification.actualLateMinutes)} within grace</span></span> : "—"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 tabular-nums">
                          {r.classification?.rawOvertimeMinutes > 0 || r.approved_overtime_minutes > 0
                            ? `${fmtMinutes(r.classification?.rawOvertimeMinutes || 0)} / ${fmtMinutes(r.approved_overtime_minutes || 0)} approved`
                            : "—"}
                          {r.classification?.unverifiedOvertimeMinutes > 0 ? (
                            <span className="block text-meta text-warning">{fmtMinutes(r.classification.unverifiedOvertimeMinutes)} waiting for a clock-out</span>
                          ) : null}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5"><Status tone={state.tone}>{state.label}</Status></td>
                        <td className="whitespace-nowrap px-4 py-2 text-right">
                          {own ? (
                            <span className="text-meta text-secondary" title="Your own attendance changes through a correction request in My HR">Yours — use My HR</span>
                          ) : canRecord ? (
                            <span className="inline-flex gap-1">
                              {r.classification?.clockOutStatus === "in_progress" ? (
                                <IconButton icon={LogOut} size="sm" label={`Clock out ${employeeOf(r)} now`} onClick={() => setConfirming({ kind: "clock-out", record: r })} />
                              ) : null}
                              {r.check_in && !r.check_out && !r.clock_out_missing && r.attention === "missing_clock_out" ? (
                                <IconButton icon={TimerOff} size="sm" label={`Mark ${employeeOf(r)}'s clock-out as missing`} onClick={() => setConfirming({ kind: "mark-missing", record: r })} />
                              ) : null}
                              <IconButton
                                icon={Edit2}
                                size="sm"
                                label={`Edit attendance for ${employeeOf(r)}`}
                                onClick={() => { setEditing({ id: r.id, business_member_id: r.business_member_id, date: normalizeDateOnly(r.date) || r.date, check_in: r.checkInLocal || "", check_out: r.checkOutLocal || "", status: r.status, notes: r.notes }); setShowManual(true); }}
                              />
                              <IconButton icon={Trash2} size="sm" label={`Delete attendance for ${employeeOf(r)}`} onClick={() => setConfirming({ kind: "delete", record: r })} />
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          </Surface>

          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} label="Attendance pages" />

          <ManualEntryModal
            open={showManual}
            onClose={() => { setShowManual(false); setEditing(null); }}
            onSubmit={handleManualSubmit}
            // A former employee stays recordable: payroll may still need their last days (ATT-1, APP-HR-058); the
            // server refuses dates whose payroll is already with Finance or final.
            members={members
              .filter((m) => ["active", "terminated"].includes(String(m.status || "").toLowerCase()) && Number(m.id) !== myMemberId)
              .sort((x, y) => Number(String(x.status).toLowerCase() === "terminated") - Number(String(y.status).toLowerCase() === "terminated"))}
            initial={editing}
          />
        </section>
      )}

      {/* C2: HR ends another employee's open session at the current time. */}
      {confirming?.kind === "clock-out" ? (
        <ConfirmDialog
          title={`Clock out ${employeeOf(confirming.record)} now?`}
          confirmLabel="Clock out"
          failureTitle="Not clocked out"
          facts={[
            { label: "Employee", value: employeeOf(confirming.record) },
            { label: "Date", value: formatDateValue(confirming.record.date) },
            { label: "Clocked in", value: fmtTime(confirming.record.checkInLocal) },
          ]}
          consequence="Their clock-out is recorded at the current time. Paid time is worked out from their schedule."
          onClose={() => setConfirming(null)}
          onConfirm={async () => {
            await businessApi.attendanceCheckOut({ business_member_id: confirming.record.business_member_id });
            setConfirming(null);
            setNotice(`${employeeOf(confirming.record)} was clocked out.`);
            load();
          }}
        />
      ) : null}

      {/* C3 (Report 70 final safeguards): closes an abandoned clock-in for payroll without inventing a clock-out time. */}
      {confirming?.kind === "mark-missing" ? (
        <RequestDecisionDialog
          title={`Mark ${employeeOf(confirming.record)}'s clock-out as missing?`}
          confirmLabel="Mark clock-out missing"
          failureTitle="Not marked"
          facts={[
            { label: "Employee", value: employeeOf(confirming.record) },
            { label: "Date", value: formatDateValue(confirming.record.date) },
            { label: "Clocked in", value: fmtTime(confirming.record.checkInLocal) },
            { label: "Scheduled end", value: confirming.record.schedule?.endTime ? fmtTime(confirming.record.schedule.endTime) : "No shift that day" },
          ]}
          consequence="No clock-out time is recorded, and this does not confirm when they left. The day is paid up to the scheduled end for now and flagged for review. Any approved overtime is not paid until the real clock-out time is entered (Edit, or the employee's correction)."
          reason={{ label: "What happened? This is kept in the activity log.", required: true }}
          onClose={() => setConfirming(null)}
          onSubmit={async ({ note }) => {
            const res = await businessApi.markAttendanceClockOutMissing(confirming.record.id, { reason: note });
            const record = res?.record || {};
            const held = record.classification?.unverifiedOvertimeMinutes > 0 ? ` ${fmtMinutes(record.classification.unverifiedOvertimeMinutes)} of approved overtime waits for the real clock-out.` : "";
            setConfirming(null);
            setNotice(`${employeeOf(confirming.record)}'s clock-out on ${formatDateValue(confirming.record.date)} is marked missing. ${record.payableUntilLocal ? `Paid until ${fmtTime(record.payableUntilLocal)} for now.` : "No paid time until a clock-out is entered."}${held}`);
            load();
          }}
        />
      ) : null}

      {/* C3: removes an attendance fact payroll may read; a reason is required and kept. */}
      {confirming?.kind === "delete" ? (
        <RequestDecisionDialog
          tone="danger"
          title={`Delete ${employeeOf(confirming.record)}'s attendance for ${formatDateValue(confirming.record.date)}?`}
          confirmLabel="Delete attendance"
          cancelLabel="Keep the record"
          failureTitle="Not deleted"
          facts={[
            { label: "Employee", value: employeeOf(confirming.record) },
            { label: "Date", value: formatDateValue(confirming.record.date) },
            { label: "Recorded", value: `${fmtTime(confirming.record.checkInLocal)} – ${confirming.record.check_out ? fmtTime(confirming.record.checkOutLocal) : "no clock-out"}` },
          ]}
          consequence="The record is removed. If that day was scheduled, it then counts as absent unless attendance is recorded again."
          reason={{ label: "Why? This is kept in the activity log.", required: true }}
          onClose={() => setConfirming(null)}
          onSubmit={async ({ note }) => {
            await businessApi.deleteAttendance(confirming.record.id, { reason: note });
            setConfirming(null);
            setNotice(`${employeeOf(confirming.record)}'s attendance for ${formatDateValue(confirming.record.date)} was deleted.`);
            load();
          }}
        />
      ) : null}

      {failure ? (
        <Dialog open size="sm" onOpenChange={(next) => { if (!next) setFailure(null); }} title={failure.title} footer={<Button onClick={() => setFailure(null)}>Close</Button>}>
          <p role="alert" className="text-body-sm text-secondary">{failure.text}</p>
        </Dialog>
      ) : null}
    </div>
  );
}
